// BEE-20: Pricing section
import type { CSSProperties } from 'react'
import { Check } from 'lucide-react'
import { Link } from 'react-router-dom'

const PLUS_BG: CSSProperties = {
  background: 'linear-gradient(160deg, #2a5040 0%, #1f4029 100%)',
}

const FREE_FEATURES = [
  'Unlimited village members',
  'Shared calendar & needs board',
  'Visit notes & insights',
  'Daily pulse updates',
]

const PLUS_FEATURES = [
  'Everything in Free',
  'Multiple care hubs',
  'Medication tracking',
  'Expense splitting',
  'Priority support',
]

export default function Pricing() {
  return (
    <section id="pricing" className="py-12 sm:py-16 px-6 bg-paper">
      <div className="max-w-[800px] mx-auto">

        {/* Section header */}
        <div className="text-center mb-8 sm:mb-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-green-700 mb-4">
            Pricing
          </p>
          <h2 className="font-serif font-normal text-[50px] tracking-[-0.02em] text-ink leading-[1.05] mb-4">
            Free to start.{' '}
            <em className="italic">Always.</em>
          </h2>
          <p className="text-[16px] text-[#4a4d47] max-w-[460px] mx-auto leading-relaxed">
            Create your canopy, invite your village, and coordinate care — no credit card required.
          </p>
        </div>

        {/* Tier cards — 2 col at 640px+, 1 col below */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

          {/* ── Free tier ── */}
          <div className="bg-card border border-[#e8e3d6] rounded-[20px] p-8 shadow-card flex flex-col">
            <h3 className="font-sans font-semibold text-[16px] text-ink mb-1">Free</h3>
            <p className="text-[13px] text-[#7a7d75] mb-7">
              Everything you need to coordinate care for one loved one.
            </p>

            <div className="mb-1">
              <span className="font-serif font-semibold text-[54px] text-ink leading-none">$0</span>
            </div>
            <p className="text-[12px] text-[#a6a89e] mb-8">forever</p>

            <ul className="space-y-3 mb-8 flex-1">
              {FREE_FEATURES.map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-[14px] text-[#4a4d47]">
                  <Check size={16} className="text-[#2d5a3d] flex-none" aria-hidden="true" strokeWidth={2.5} />
                  {item}
                </li>
              ))}
            </ul>

            <Link
              to="/enroll"
              className="block w-full py-[13px] rounded-full bg-[#1f4029] text-white text-[15px] font-semibold text-center hover:bg-[#2d5a3d] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
            >
              Get started
            </Link>
          </div>

          {/* ── Plus tier ── */}
          <div
            style={PLUS_BG}
            className="rounded-[20px] p-8 relative overflow-hidden flex flex-col"
          >
            {/* Decorative arc */}
            <div
              className="absolute right-[-44px] top-[-44px] w-[180px] h-[180px] border-[14px] border-[rgba(200,155,60,.18)] rounded-full pointer-events-none"
              aria-hidden="true"
            />

            {/* Coming soon badge */}
            <div className="inline-flex items-center self-start px-3 py-[4px] rounded-full bg-[rgba(200,155,60,.18)] border border-[rgba(200,155,60,.28)] text-[10.5px] font-bold text-[#f0dfa6] tracking-[0.1em] uppercase mb-5">
              Coming soon
            </div>

            <h3 className="font-sans font-semibold text-[16px] text-white mb-1">Plus</h3>
            <p className="text-[13px] text-[#8db09a] mb-7">
              Advanced features for complex care coordination.
            </p>

            <div className="mb-1">
              <span className="font-serif font-semibold text-[54px] text-white leading-none">$12</span>
            </div>
            <p className="text-[12px] text-[#7a9e87] mb-8">per month</p>

            <ul className="space-y-3 mb-8 flex-1">
              {PLUS_FEATURES.map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-[14px] text-[#c0d8c9]">
                  <Check size={16} className="text-[#c89b3c] flex-none" aria-hidden="true" strokeWidth={2.5} />
                  {item}
                </li>
              ))}
            </ul>

            <a
              href="#"
              className="block w-full py-[13px] rounded-full border border-[rgba(255,255,255,.22)] text-white text-[15px] font-semibold text-center hover:bg-[rgba(255,255,255,.08)] transition-colors duration-150 relative z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1f4029]"
            >
              Join waitlist
            </a>
          </div>

        </div>
      </div>
    </section>
  )
}
